// Dashboard: one page of the queue's CURRENT STATE. Read-only; it writes
// nothing, and it takes no filters (D-460).
//
// REBUILT 2026-11-04 (owner — D-461), and REVISED THE SAME DAY (owner — D-469:
// colour the pipeline bars by age as a share of each step's own count, put
// Denial Review in line, drop the intro line and the In Progress Now section,
// give the two age bars sections of their own, and replace This Week with the
// payment cycle, Wednesday to Tuesday, because Point Click Care exports run on
// Tuesdays). Top to bottom:
//
//  1. THE PIPELINE NOW (`components/PipelineFlow.tsx`): a card per step, Denial
//     Review in the same row, each with its count, dollars and a full-width bar
//     split by uploaded age.
//  2. DAYS SINCE INVOICE DATE and DAYS SINCE UPLOADED DATE: two sections side
//     by side, each a prominent age bar over the invoices in progress.
//  3. THIS PAYMENT CYCLE: the four standing figures (§16.9, counted since
//     Wednesday at midnight Eastern), then Invoices In and Out, Last 8 Payment
//     Cycles (`components/InOutChart.tsx`, §16.10's `cycle` grain).
//
// Every figure is an aggregation endpoint, already scoped by the API to the
// rows this user may see. Nothing here is computed from rows the browser holds.
import { useEffect, useMemo, useState } from 'react'
import { Caption1, Subtitle2, Text, makeStyles, tokens } from '@fluentui/react-components'
import { errorMessage } from '../api/errors'
import type { AgeBandTile, AnalyticsByDimension, AnalyticsKpis, AnalyticsKpiTile, AnalyticsParams } from '../api/types'
import { loadAnalyticsByDimension, loadAnalyticsKpis, loadAnalyticsOverTime } from '../lib/data'
import { STATUS_OPTIONS, emptyCriteria, toAnalyticsParams, type Criteria } from '../lib/criteria'
import { formatCurrency } from '../lib/format'
import { KpiCard } from '../components/DashboardCharts'
import { AgeProgressBar } from '../components/AgeProgressBar'
import { PipelineFlow } from '../components/PipelineFlow'
import { InOutChart } from '../components/InOutChart'
import { paymentCycleColumns, type InOutDay, type StepFigures } from '../lib/dashboardFigures'
import { Loading, ErrorBanner } from '../components/States'

const DAY_MS = 24 * 60 * 60 * 1000

/** Every status short of Archives: the "In progress" group of the Status control. */
const IN_PROGRESS: Criteria = {
  ...emptyCriteria,
  statuses: STATUS_OPTIONS.filter((option) => option.group === 'In progress').map((option) => option.value),
}
const IN_PROGRESS_PARAMS = toAnalyticsParams(IN_PROGRESS)

/**
 * The invoice-date bands: the usual accounts-payable aging buckets. Invoices
 * often arrive weeks after their date, so the uploaded-date bands (0-1 to 7+)
 * would put nearly every invoice in the last one.
 */
const INVOICE_DATE_BANDS: readonly { label: string; min_days: number; max_days: number | null }[] = [
  { label: '0-30', min_days: 0, max_days: 30 },
  { label: '31-60', min_days: 31, max_days: 60 },
  { label: '61-90', min_days: 61, max_days: 90 },
  { label: '91+', min_days: 91, max_days: null },
]

/** Today's date in Eastern time, `YYYY-MM-DD`. */
function easternToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date())
}

/** `date` moved by `days` (negative is earlier), both `YYYY-MM-DD`. */
function shiftDate(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10)
}

/** "Wednesday, November 4": the day the current payment cycle began, from §16.9's `week_start`. */
const cycleStartLabel = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' })

/** An upload window holding the in-progress invoices whose whole-day age falls in the band. */
function uploadedBandParams(band: AgeBandTile, now: number): AnalyticsParams {
  const params: AnalyticsParams = { ...IN_PROGRESS_PARAMS }
  if (band.min_days > 0) params.uploaded_to = new Date(now - band.min_days * DAY_MS).toISOString()
  if (band.max_days !== null) params.uploaded_from = new Date(now - (band.max_days + 1) * DAY_MS + 1).toISOString()
  return params
}

const sumCount = (rows: AnalyticsByDimension | null) => (rows?.rows ?? []).reduce((n, r) => n + r.invoice_count, 0)
const sumTotal = (rows: AnalyticsByDimension | null) => (rows?.rows ?? []).reduce((n, r) => n + r.total, 0)

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '24px', paddingTop: '8px' },
  sub: { color: tokens.colorNeutralForeground3 },
  // A section's surface; its heading sits outside the border (owner, 2026-09-15).
  kpiBlock: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: '16px 20px',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
  },
  // The in-and-out chart under the cycle's tiles, in the same box, past a rule.
  cycleChart: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  section: { display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 },
  sectionHead: { display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' },
  // THE TWO AGE SECTIONS SIDE BY SIDE; stacked below 1100px.
  ageRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: '24px',
    '@media (min-width: 1100px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
})

interface Props {
  refreshNonce?: number
}

export function DashboardScreen({ refreshNonce = 0 }: Props) {
  const s = useStyles()
  const [kpis, setKpis] = useState<AnalyticsKpis | null>(null)
  // A FAILED KPI READ SAYS SO (code review H-07, 2026-10-17). Retry re-reads it alone.
  const [kpiError, setKpiError] = useState<string | null>(null)
  const [kpiRetry, setKpiRetry] = useState(0)
  const [step, setStep] = useState<AnalyticsByDimension | null>(null)
  const [loading, setLoading] = useState(true)
  // Per uploaded-age band, the in-progress invoices per step: the pipeline cards' colours.
  const [uploadedSplit, setUploadedSplit] = useState<AnalyticsByDimension[] | null>(null)
  // Per invoice-date band, the in-progress invoices per step, summed for the bar.
  const [invoiceDateSplit, setInvoiceDateSplit] = useState<(AnalyticsByDimension | null)[] | null>(null)
  // Undefined while loading; null when the read failed.
  const [inOut, setInOut] = useState<InOutDay[] | null | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    setKpiError(null)
    loadAnalyticsKpis()
      .then((next) => {
        if (!cancelled) setKpis(next)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setKpis(null)
        setKpiError(errorMessage(e))
      })
    return () => {
      cancelled = true
    }
  }, [refreshNonce, kpiRetry])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    loadAnalyticsByDimension('step', IN_PROGRESS_PARAMS)
      .then((next) => {
        if (!cancelled) setStep(next)
      })
      .catch(() => {
        if (!cancelled) setStep(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [refreshNonce])

  // THE UPLOADED-AGE SPLIT waits for the KPI read, whose bands it reuses, so the
  // cards and the Days Since Uploaded Date bar cut age at the same places.
  useEffect(() => {
    if (kpis === null) return
    let cancelled = false
    const now = Date.now()
    void Promise.allSettled(
      kpis.outstanding_by_age.map((band) => loadAnalyticsByDimension('step', uploadedBandParams(band, now))),
    ).then((results) => {
      if (cancelled) return
      setUploadedSplit(
        results.every((r) => r.status === 'fulfilled')
          ? results.map((r) => (r as PromiseFulfilledResult<AnalyticsByDimension>).value)
          : null,
      )
    })
    return () => {
      cancelled = true
    }
  }, [kpis])

  // THE INVOICE-DATE SPLIT: one in-progress read per band, bounded by invoice
  // date. The youngest band has no upper bound, so a future-dated invoice
  // counts there rather than nowhere.
  useEffect(() => {
    let cancelled = false
    const today = easternToday()
    void Promise.allSettled(
      INVOICE_DATE_BANDS.map((band) =>
        loadAnalyticsByDimension(
          'step',
          toAnalyticsParams({
            ...IN_PROGRESS,
            invoiceStart: band.max_days === null ? '' : shiftDate(today, -band.max_days),
            invoiceEnd: band.min_days === 0 ? '' : shiftDate(today, -band.min_days),
          }),
        ),
      ),
    ).then((results) => {
      if (cancelled) return
      setInvoiceDateSplit(results.map((r) => (r.status === 'fulfilled' ? r.value : null)))
    })
    return () => {
      cancelled = true
    }
  }, [refreshNonce])

  // IN AND OUT PER PAYMENT CYCLE (D-469): one Over Time read at the `cycle`
  // grain, which answers the last eight Wednesday-to-Tuesday cycles, the
  // current one last, each with its uploads and its exports.
  useEffect(() => {
    let cancelled = false
    setInOut(undefined)
    loadAnalyticsOverTime('cycle')
      .then((answer) => {
        if (!cancelled) setInOut(paymentCycleColumns(answer.periods))
      })
      .catch(() => {
        if (!cancelled) setInOut(null)
      })
    return () => {
      cancelled = true
    }
  }, [refreshNonce])

  const figures = useMemo(() => {
    const out = new Map<string, StepFigures>()
    for (const row of step?.rows ?? []) {
      const ages =
        uploadedSplit === null
          ? null
          : uploadedSplit.map((band) => band.rows.find((r) => r.key === row.key)?.invoice_count ?? 0)
      out.set(row.key, { count: row.invoice_count, total: row.total, medianDays: row.median_days, ages })
    }
    return out
  }, [step, uploadedSplit])

  // The invoice-date bar's bands, and what none of them caught (no invoice date).
  const invoiceDateBar = useMemo((): { bands: AgeBandTile[]; unknown: AnalyticsKpiTile } | null => {
    if (invoiceDateSplit === null || step === null || invoiceDateSplit.some((r) => r === null)) return null
    const bands = INVOICE_DATE_BANDS.map((band, i) => ({
      ...band,
      count: sumCount(invoiceDateSplit[i]!),
      total: sumTotal(invoiceDateSplit[i]!),
    })) as AgeBandTile[]
    const counted = bands.reduce((n, b) => n + b.count, 0)
    const totalled = bands.reduce((n, b) => n + b.total, 0)
    return {
      bands,
      unknown: { count: Math.max(0, sumCount(step) - counted), total: Math.max(0, sumTotal(step) - totalled) } as AnalyticsKpiTile,
    }
  }, [invoiceDateSplit, step])

  // The invoice-date bar needs the step read and all four splits; either failing fails it.
  const invoiceDateFailed =
    (!loading && step === null) || (invoiceDateSplit !== null && invoiceDateSplit.some((r) => r === null))

  return (
    <div className={s.root}>
      <div className={s.section}>
        <div className={s.sectionHead}>
          <Subtitle2>The Pipeline Now</Subtitle2>
        </div>
        <div className={s.kpiBlock}>
          {loading ? (
            <Loading label="Loading the pipeline…" />
          ) : step === null ? (
            <Caption1 className={s.sub}>The pipeline could not be loaded.</Caption1>
          ) : (
            <PipelineFlow figures={figures} bands={kpis?.outstanding_by_age ?? []} />
          )}
        </div>
      </div>

      {/* Two bars over the same invoices in progress, aged on two clocks: when
          the vendor dated the invoice, and when it reached us. */}
      <div className={s.ageRow}>
        <div className={s.section}>
          <div className={s.sectionHead}>
            <Subtitle2>Days Since Invoice Date</Subtitle2>
          </div>
          <div className={s.kpiBlock}>
            {invoiceDateBar !== null ? (
              <AgeProgressBar
                prominent
                title="Days Since Invoice Date"
                unknownLabel="No invoice date"
                bands={invoiceDateBar.bands}
                unknown={invoiceDateBar.unknown}
              />
            ) : invoiceDateFailed ? (
              <Caption1 className={s.sub}>Days Since Invoice Date could not be loaded.</Caption1>
            ) : (
              <Caption1 className={s.sub}>Loading…</Caption1>
            )}
          </div>
        </div>
        <div className={s.section}>
          <div className={s.sectionHead}>
            <Subtitle2>Days Since Uploaded Date</Subtitle2>
          </div>
          <div className={s.kpiBlock}>
            {kpis !== null ? (
              <AgeProgressBar prominent bands={kpis.outstanding_by_age} unknown={kpis.outstanding_unknown_age} />
            ) : kpiError !== null ? (
              <Caption1 className={s.sub}>Days Since Uploaded Date could not be loaded.</Caption1>
            ) : (
              <Caption1 className={s.sub}>Loading…</Caption1>
            )}
          </div>
        </div>
      </div>

      <div className={s.section}>
        <div className={s.sectionHead}>
          <Subtitle2>This Payment Cycle</Subtitle2>
          {kpis !== null && kpis.week_start !== '' && (
            <Caption1 className={s.sub}>
              Since {cycleStartLabel.format(new Date(`${kpis.week_start}T00:00:00Z`))}
            </Caption1>
          )}
        </div>
        <div className={s.kpiBlock}>
          {kpiError !== null ? (
            <ErrorBanner message={kpiError} onRetry={() => setKpiRetry((n) => n + 1)} />
          ) : kpis === null ? (
            <Caption1 className={s.sub}>Loading…</Caption1>
          ) : (
            <div className={s.kpiGrid}>
              <KpiCard
                label="Invoices Imported This Payment Cycle"
                value={String(kpis.imported_this_week.count)}
                sub={formatCurrency(kpis.imported_this_week.total)}
              />
              <KpiCard
                label="Invoices in Progress"
                value={String(kpis.in_progress.count)}
                sub={formatCurrency(kpis.in_progress.total)}
              />
              <KpiCard
                label="Invoices Past Due"
                value={String(kpis.past_due.count)}
                sub={`uploaded over ${kpis.past_due_days} days ago · ${formatCurrency(kpis.past_due.total)}`}
                subDanger={kpis.past_due.count > 0}
              />
              <KpiCard
                label="Invoices Exported This Payment Cycle"
                value={String(kpis.exported_this_week.count)}
                sub={formatCurrency(kpis.exported_this_week.total)}
              />
            </div>
          )}
          <div className={s.cycleChart}>
            <Text weight="semibold">Invoices In and Out, Last 8 Payment Cycles</Text>
            {inOut === undefined ? (
              <Caption1 className={s.sub}>Loading…</Caption1>
            ) : inOut === null ? (
              <Caption1 className={s.sub}>Invoices in and out could not be loaded.</Caption1>
            ) : (
              <InOutChart days={inOut} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
